import { SignInPage } from '@ohpen/react-auth';
import { getCdnAssets, useConfig, type CdnAssets } from '@ohpen/react-utils';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { PageSpinner } from '@ohpen/enso-ui';

export const Login = () => {
  const config = useConfig();
  const auth = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const providerIds = config?.AWS?.IDENTITY_PROVIDERS || [];
  const providers = providerIds.map(id => ({ id, name: id, type: 'azure' as const }));

  const [cdnAssets, setCdnAssets] = useState<CdnAssets | null>(null);

  useEffect(() => {
    getCdnAssets(config).then(assets => {
      if (assets) setCdnAssets(assets);
    });
  }, [config]);

  // Enable credentials login based on config.
  const enableCredentials = config?.AWS?.ENABLE_CREDENTIALS_LOGIN ?? false;
  const hasSSO = providers.length > 0;

  // The URL the user was trying to access before being redirected to login.
  // For SSO the destination is preserved via the OIDC state parameter, not sessionStorage.
  const routerFrom = (location.state as { from?: string } | null)?.from;
  const from = routerFrom || '/';

  // Show spinner while checking if already authenticated, to avoid a flash of the login form.
  // OIDC auth is async (loads from localStorage); Amplify credentials auth is also async.
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Wait until OIDC has finished loading before checking auth state.
    if (auth.isLoading) return;

    const checkAuth = async () => {
      // OIDC (SSO) is authenticated — navigate immediately.
      if (auth.isAuthenticated) {
        navigate(from, { replace: true });
        return;
      }

      // Check Amplify credentials auth.
      if (enableCredentials) {
        try {
          await getCurrentUser();
          navigate(from, { replace: true });
          return;
        } catch {
          // Not authenticated via credentials either — show the login form.
        }
      }

      setChecking(false);
    };

    checkAuth();
  }, [auth.isLoading, auth.isAuthenticated, enableCredentials, navigate, from]);

  if (checking) return <PageSpinner />;

  const handleProviderLogin = async (providerId: string) => {
    await auth.signinRedirect({
      extraQueryParams: { identity_provider: providerId },
      state: { from },
    });
  };

  const handleAfterSignIn = async () => {
    navigate(from, { replace: true });
  };

  // Determine description based on available login methods
  const getDescription = () => {
    if (hasSSO && enableCredentials) return t('login.descriptionSsoAndCredentials');
    if (enableCredentials) return t('login.descriptionCredentialsOnly');
    return t('login.descriptionSsoOnly');
  };

  return (
    <SignInPage
      logo={cdnAssets?.logoPath ?? ''}
      description={getDescription()}
      providers={providers}
      enableCredentials={enableCredentials}
      onProviderLogin={handleProviderLogin}
      onAfterSignIn={handleAfterSignIn}
      useAuthContext={false}
    />
  );
};
